import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  CustomFieldRow,
  CustomFieldColumn,
  GradeDeleteIconButton,
  MultiValueWrapper
} from './styled';
import Icons from '../../components/icons';
import MultiValueInput from '../../components/MultiValueInput';
import { SchoolStructureData } from './types';
import { Control, Controller, Path, UseFormSetValue } from 'react-hook-form';
import DropdownSelect from '../../components/DropdownSelect';
import { boardOptions } from './config/constant';
import TextInputWithIcon from '../../components/TextInputWithIcon';

interface GradeRowProps {
  sectionKey: string;
  gradeIndex: number;
  onRemove: (index: number) => void;
  control: Control<SchoolStructureData>;
  setValue: UseFormSetValue<SchoolStructureData>;
}

const GradeRow: React.FC<GradeRowProps> = ({
  sectionKey,
  gradeIndex,
  onRemove,
  control,
  setValue
}) => {
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const watch = control?._getWatch;

  const baseGradePath = useMemo(
    () => `sections.${sectionKey}.grades.${gradeIndex}`,
    [sectionKey, gradeIndex]
  );

  const grade = watch(baseGradePath as any);

  return (
    <CustomFieldRow>
      <CustomFieldColumn>
        <Controller
          name={`${baseGradePath}.name` as Path<SchoolStructureData>}
          control={control}
          render={({ field, fieldState }) => (
            <TextInputWithIcon
              field={field}
              fullWidth
              label='Grade Name'
              variant='outlined'
              error={!!fieldState.error}
              helperText={fieldState.error?.message || ''}
              onChange={(e) => {
                field.onChange(e);
              }}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                field.onChange(trimmed);
                field.onBlur();

                if (!displayNameTouched) {
                  setValue(
                    `${baseGradePath}.displayName` as Path<SchoolStructureData>,
                    trimmed,
                    { shouldValidate: true }
                  );
                }
              }}
            />
          )}
        />
      </CustomFieldColumn>
      <CustomFieldColumn>
        <Controller
          name={`${baseGradePath}.displayName` as Path<SchoolStructureData>}
          control={control}
          render={({ field, fieldState }) => (
            <TextInputWithIcon
              field={field}
              fullWidth
              label='Display Name'
              variant='outlined'
              error={!!fieldState.error}
              helperText={fieldState.error?.message || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setDisplayNameTouched(false);
                } else {
                  setDisplayNameTouched(true);
                }
                field.onChange(e);
              }}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                field.onChange(trimmed);
                field.onBlur();
              }}
            />
          )}
        />
      </CustomFieldColumn>
      <MultiValueWrapper>
        <Controller
          name={`${baseGradePath}.divisions` as Path<SchoolStructureData>}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MultiValueInput
              label='Divisions'
              placeholder='Type division and press Enter'
              values={(field.value as string[]) || []}
              onAddValue={(newValue) =>
                field.onChange([...((field.value as string[]) || []), newValue])
              }
              onRemoveValue={(indexToRemove) =>
                field.onChange(
                  ((field.value as string[]) || []).filter(
                    (_: string, i: number) => i !== indexToRemove
                  )
                )
              }
              error={!!error}
              helperText={error?.message || ''}
            />
          )}
        />
      </MultiValueWrapper>
      <Controller
        name={`${baseGradePath}.board` as Path<SchoolStructureData>}
        control={control}
        render={({ field, fieldState }) => (
          <Box sx={{ width: { xs: '100%', sm: '190px' } }}>
            <DropdownSelect
              {...field}
              label='Board'
              placeholder='Select Board'
              options={boardOptions}
              value={typeof field.value === 'string' ? field.value : ''}
              error={!!fieldState.error}
            />
          </Box>
        )}
      />
      <GradeDeleteIconButton
        onClick={() => onRemove(gradeIndex)}
        aria-label={`Delete grade ${grade?.name || gradeIndex + 1}`}
      >
        <Icons.Delete />
      </GradeDeleteIconButton>
    </CustomFieldRow>
  );
};

export default React.memo(GradeRow);
